// game/entities.ts — entity bodies and their pools.
// Everything is recycled: `live` is a dense array (swap-removed), `free` holds
// retired objects. Nothing in the hot path allocates.

import type { EliteRider, Enemy, EnemyDef, GeneratorDef, Pickup, ProjectileDef, Vec } from '../types'

// ---------------------------------------------------------------- pool

export class Pool<T> {
  readonly live: T[] = []
  private readonly free: T[] = []

  constructor(private readonly make: () => T) {}

  obtain(): T {
    const o = this.free.pop() ?? this.make()
    this.live.push(o)
    return o
  }

  /** Swap-remove index i. Safe to call while iterating backwards. */
  releaseAt(i: number): void {
    const last = this.live.pop()
    if (last === undefined) return
    if (i < this.live.length) {
      const o = this.live[i]
      this.live[i] = last
      this.free.push(o)
    } else {
      this.free.push(last)
    }
  }

  clear(): void {
    while (this.live.length) this.free.push(this.live.pop() as T)
  }

  get count(): number {
    return this.live.length
  }
}

// ---------------------------------------------------------------- hooks
// Enemy.kill() is part of the public contract and can be called by content, so
// the death consequences (loot, embers, particles) are routed through a hook the
// run installs. Keeps entities.ts free of imports from the run.

export interface EntityHooks {
  onEnemyDeath(e: EnemyEnt, silent: boolean): void
}

let hooks: EntityHooks | null = null

export function setEntityHooks(h: EntityHooks | null): void {
  hooks = h
}

// ---------------------------------------------------------------- enemy

export class EnemyEnt implements Enemy {
  def!: EnemyDef
  pos: Vec = { x: 0, y: 0 }
  vel: Vec = { x: 0, y: 0 }
  hp = 1
  maxHp = 1
  alive = false
  elite: EliteRider | null = null
  tags: Record<string, number | boolean | string | undefined> = {}
  /** Seconds pinned with non-zero intent and zero displacement (D-015 anti-wedge). */
  stuckT = 0

  // runtime
  radius = 8
  speed = 60
  touchDamage = 5
  spriteId = ''
  facing = 1
  flash = 0
  /** role brain state machine */
  state = 0
  stateT = 0
  cool = 0
  /** attack windup remaining (>0 means telegraphing) */
  windup = 0
  telegraph = 0
  scale = 1
  /** index into run.generators, -1 for patrols/scripted spawns */
  genIndex = -1
  /** knockback impulse decayed by the mover */
  kbx = 0
  kby = 0
  bob = 0
  hitFlashT = 0
  shielded = false
  spawnGrace = 0

  kill(silent?: boolean): void {
    if (!this.alive) return
    this.alive = false
    hooks?.onEnemyDeath(this, silent === true)
  }

  reset(): void {
    this.elite = null
    this.state = 0
    this.stateT = 0
    this.cool = 0
    this.windup = 0
    this.telegraph = 0
    this.scale = 1
    this.genIndex = -1
    this.kbx = 0
    this.kby = 0
    this.stuckT = 0
    this.flash = 0
    this.hitFlashT = 0
    this.shielded = false
    this.facing = 1
    this.vel.x = 0
    this.vel.y = 0
    this.spawnGrace = 0
    for (const k in this.tags) delete this.tags[k]
  }
}

// ---------------------------------------------------------------- projectile

export class ProjectileEnt {
  def!: ProjectileDef
  pos: Vec = { x: 0, y: 0 }
  vel: Vec = { x: 0, y: 0 }
  alive = false
  friendly = false
  life = 0
  damage = 0
  radius = 3
  pierce = 0
  bounces = 0
  knockback = 0
  rot = 0
  color = '#fff'
  spriteId = ''
  /** lobbed arc bookkeeping */
  startX = 0
  startY = 0
  targetX = 0
  targetY = 0
  travel = 0
  totalDist = 1
  arcH = 0
  splash = 0
  splashDamage = 0
  /** enemies already pierced this flight (ids kept small; reused array) */
  hitList: EnemyEnt[] = []
}

// ---------------------------------------------------------------- pickup

export class PickupEnt implements Pickup {
  kind: Pickup['kind'] = 'gold'
  pos: Vec = { x: 0, y: 0 }
  value = 0
  tag?: string
  alive = false
  bob = 0
  spriteId = ''
  life = -1 // <0 = permanent
}

// ---------------------------------------------------------------- hazard
// Ground effects: the Cinder Blade's burn trail today; flood tiles and brazier
// light later. Circle + dps + expiry, nothing more.

export class HazardEnt {
  x = 0
  y = 0
  r = 20
  dps = 0
  until = 0
  color = '#ff7a2a'
  friendly = true
  alive = false
  tick = 0
  born = 0
}

// ---------------------------------------------------------------- registries

export const PROJECTILES = new Map<string, ProjectileDef>()

export function registerProjectile(def: ProjectileDef): void {
  PROJECTILES.set(def.id, def)
}

export const ENEMY_DEFS = new Map<string, EnemyDef>()

export function registerEnemyDef(def: EnemyDef): void {
  ENEMY_DEFS.set(def.id, def)
}

export const GENERATOR_DEFS = new Map<string, GeneratorDef>()

export function registerGeneratorDef(def: GeneratorDef): void {
  GENERATOR_DEFS.set(def.id, def)
}
